import { RRuleSet, rrulestr } from 'rrule';
import { supabase, getAuthHeaders, API_BASE } from '../utils/supabase/client';
import { DataService } from './DataService';
import type {
  CalendarEvent,
  CreateCalendarEventData,
  UpdateCalendarEventData,
  CalendarTaskBlock,
} from '../types/calendar';
import type { Database as SupabaseDatabase } from '../types/generated/database';
import { createCalendarEventReads, toCalendarEvent } from './calendarEventReads';

type CalendarEventRow = SupabaseDatabase['public']['Tables']['calendar_events']['Row'] & {
  is_flexible_task?: boolean | null;
  reminders?: number[] | null;
};
type CalendarEventInsert = SupabaseDatabase['public']['Tables']['calendar_events']['Insert'] & {
  is_flexible_task?: boolean;
  reminders?: number[];
};

export interface FlexibleTaskBlockMove {
  manualOperation?: boolean;
  workspaceId: string;
  calendar: { from: string; to: string };
  eventId: string;
  expectedUpdatedAt: string;
  startTime: string;
  endTime: string;
}

export interface FlexibleTaskBlockMoveReceipt {
  calendarChange: {
    eventId: string;
    taskId: string;
    startTime: string;
    endTime: string;
    updatedAt: string;
  };
  event: Pick<CalendarEvent, 'id' | 'start_time' | 'end_time' | 'updated_at'>;
  moved: true;
}

function isMoveReceipt(value: unknown, request: FlexibleTaskBlockMove): value is FlexibleTaskBlockMoveReceipt {
  if (!value || typeof value !== 'object') return false;
  const reply = value as Partial<FlexibleTaskBlockMoveReceipt>;
  const change = reply.calendarChange;
  const event = reply.event;
  if (!change || !event) return false;
  return reply.moved === true
    && change.eventId === request.eventId && event.id === request.eventId
    && typeof change.taskId === 'string' && change.taskId.length > 0
    && typeof change.updatedAt === 'string' && Number.isFinite(Date.parse(change.updatedAt))
    && event.updated_at === change.updatedAt
    && Date.parse(change.startTime) === Date.parse(request.startTime)
    && Date.parse(change.endTime) === Date.parse(request.endTime)
    && Date.parse(event.start_time) === Date.parse(request.startTime)
    && Date.parse(event.end_time) === Date.parse(request.endTime);
}

export function validateCalendarReminders(value: unknown): number[] {
  const allowed = new Set([0, 5, 10, 15, 30, 60, 120, 1440, 2880, 10080]);
  if (!Array.isArray(value) || value.length > allowed.size || value.some(minutes => !Number.isInteger(minutes) || !allowed.has(minutes))) {
    throw new Error('Choose valid calendar reminder times');
  }
  return [...new Set(value as number[])].sort((a, b) => a - b);
}

export class CalendarServiceClass {
  private reminderCapabilities = new Map<string, { available: boolean; expiresAt: number }>();
  private reminderProbes = new Map<string, Promise<boolean>>();
  private reminderSchemaAvailable = false;

  async getReminderAvailability(userId: string): Promise<boolean> {
    const cached = this.reminderCapabilities.get(userId);
    if (cached && cached.expiresAt > Date.now()) {
      this.reminderSchemaAvailable = cached.available;
      return cached.available;
    }
    let probe = this.reminderProbes.get(userId);
    if (!probe) {
      probe = (async () => {
        try {
          const snapshot = await supabase.from('calendar_events').select('*').eq('user_id', userId).limit(1);
          if (snapshot.error) return false;
          let available: boolean;
          if (snapshot.data?.length) available = Object.prototype.hasOwnProperty.call(snapshot.data[0], 'reminders');
          else {
            const { error } = await supabase.from('calendar_events').select('reminders').eq('user_id', userId).limit(1);
            if (error && !((error.code === '42703' || error.code === 'PGRST204') && error.message.includes('reminders'))) return false;
            available = !error;
          }
          if (this.reminderCapabilities.size >= 32) this.reminderCapabilities.delete(this.reminderCapabilities.keys().next().value!);
          this.reminderCapabilities.set(userId, { available, expiresAt: Date.now() + 5 * 60_000 });
          return available;
        } catch { return false; }
      })();
      this.reminderProbes.set(userId, probe);
    }
    const available = await probe;
    if (this.reminderProbes.get(userId) === probe) this.reminderProbes.delete(userId);
    this.reminderSchemaAvailable = available;
    return available;
  }

  private prepareReminderWrite(fields: { reminders?: number[] }): void {
    if (fields.reminders === undefined) return;
    fields.reminders = validateCalendarReminders(fields.reminders);
    if (!this.reminderSchemaAvailable) {
      if (fields.reminders.length) throw new Error('Calendar reminders are unavailable on this server');
      delete fields.reminders;
    }
  }

  private reads = createCalendarEventReads(supabase);

  private flexibleTaskSchema: 'available' | 'legacy' | 'unavailable' = 'unavailable';
  private flexibilityCapabilities = new Map<string, { state: 'available' | 'legacy'; expiresAt: number }>();
  private flexibilityProbes = new Map<string, Promise<'available' | 'legacy' | 'unavailable'>>();

  private toCalendarEvent(row: CalendarEventRow): CalendarEvent {
    return toCalendarEvent(row);
  }

  async getEventById(userId: string, eventId: string): Promise<CalendarEvent | null> {
    const { data, error } = await supabase.from('calendar_events').select('*')
      .eq('user_id', userId).eq('id', eventId).maybeSingle();
    if (error) throw error;
    return data ? this.toCalendarEvent(data) : null;
  }

  async getEventOccurrenceByStart(userId: string, masterId: string, occurrenceStart: string): Promise<CalendarEvent | null> {
    const { data, error } = await supabase.from('calendar_events').select('*')
      .eq('user_id', userId).eq('master_event_id', masterId).eq('original_start_time', occurrenceStart).maybeSingle();
    if (error) throw error;
    return data ? this.toCalendarEvent(data) : null;
  }

  getEvents(userId: string, from: Date, to: Date): Promise<CalendarEvent[]> {
    return this.reads.getEvents(userId, from, to);
  }

  async createEvent(userId: string, event: CreateCalendarEventData, options?: { manualOperation?: boolean }): Promise<CalendarEvent> {
    if (event.is_flexible_task === true && this.flexibleTaskSchema !== 'available') {
      throw new Error('Automatic scheduling is unavailable on this server');
    }
    const row: CalendarEventInsert = {
      ...event,
      is_flexible_task: event.is_flexible_task === true,
      user_id: userId,
      color: event.color ?? 'purple',
    };
    this.prepareReminderWrite(row);
    if (this.flexibleTaskSchema === 'legacy') {
      if (event.is_flexible_task === true) throw new Error('Automatic scheduling is unavailable on this server');
      delete row.is_flexible_task;
    }
    if (event.is_flexible_task === true && options?.manualOperation === true) {
      // Forward migration RPC is absent from the generated pre-release schema.
      const manualRpc = supabase as unknown as { rpc(name: string, args: Record<string, unknown>): PromiseLike<{ data: unknown; error: { code: string; message: string } | null }> };
      const { data, error } = await manualRpc.rpc('create_manual_calendar_flexible_block', { p_user_id: userId, p_event: row });
      if (!error) return this.toCalendarEvent(data as CalendarEventRow);
      if (error.code !== 'PGRST202') throw error;
    }
    const { data, error } = await supabase
      .from('calendar_events')
      // The release regenerates schema types; this cast preserves the new runtime provenance field.
      .insert(row as SupabaseDatabase['public']['Tables']['calendar_events']['Insert'])
      .select()
      .single();

    if (error) throw error;
    return this.toCalendarEvent(data);
  }

  async updateEvent(event: UpdateCalendarEventData): Promise<CalendarEvent> {
    const { id, ...fields } = event;
    this.prepareReminderWrite(fields);
    if (this.flexibleTaskSchema === 'legacy' && fields.is_flexible_task !== undefined) {
      if (fields.is_flexible_task === true) throw new Error('Automatic scheduling is unavailable on this server');
      delete fields.is_flexible_task;
    }
    const { data, error } = await supabase
      .from('calendar_events')
      .update(fields as SupabaseDatabase['public']['Tables']['calendar_events']['Update'])
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return this.toCalendarEvent(data);
  }

  async getFlexibleTaskSchedulingAvailability(userId: string): Promise<boolean> {
    const cached = this.flexibilityCapabilities.get(userId);
    if (cached && cached.expiresAt > Date.now()) {
      this.flexibleTaskSchema = cached.state;
      return cached.state === 'available';
    }
    let probe = this.flexibilityProbes.get(userId);
    if (!probe) {
      probe = this.probeFlexibilityCapability(userId);
      this.flexibilityProbes.set(userId, probe);
    }
    const state = await probe;
    if (this.flexibilityProbes.get(userId) === probe) {
      this.flexibilityProbes.delete(userId);
      if (state !== 'unavailable') {
        this.flexibilityCapabilities.set(userId, { state, expiresAt: Date.now() + 5 * 60_000 });
      }
    }
    this.flexibleTaskSchema = state;
    return state === 'available';
  }

  private async probeFlexibilityCapability(userId: string): Promise<'available' | 'legacy' | 'unavailable'> {
    try {
      const snapshot = await supabase.from('calendar_events').select('*').eq('user_id', userId).limit(1);
      if (snapshot.error) return 'unavailable';
      if (snapshot.data?.length) {
        return Object.prototype.hasOwnProperty.call(snapshot.data[0], 'is_flexible_task') ? 'available' : 'legacy';
      }
      // An empty actor calendar has no row keys from which to infer the schema.
      const { error } = await supabase.from('calendar_events').select('is_flexible_task').eq('user_id', userId).limit(1);
      if (!error) return 'available';
      const missingColumn = (error.code === '42703' || error.code === 'PGRST204')
        && error.message.includes('is_flexible_task');
      return missingColumn ? 'legacy' : 'unavailable';
    } catch {
      return 'unavailable';
    }
  }

  async moveFlexibleTaskBlock(request: FlexibleTaskBlockMove): Promise<FlexibleTaskBlockMoveReceipt> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/si/calendar/move`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });
    if (!res.ok) {
      if (res.status === 404) throw new Error('Automatic scheduling is unavailable on this server');
      const message = await res.text().catch(() => '');
      throw new Error(`Task block move failed: ${res.status} ${message}`);
    }
    const receipt: unknown = await res.json();
    if (!isMoveReceipt(receipt, request)) throw new Error('The server did not confirm the task block move');
    return receipt;
  }

  async deleteEvent(id: string): Promise<void> {
    const { error } = await supabase
      .from('calendar_events')
      .delete()
      .eq('id', id);

    if (error) throw error;
  }

  // Re-inserts a previously deleted event. The server generates a fresh id
  // (the deleted row's id is gone from the DB) so the restored event will
  // have a new id — callers should not rely on the original id persisting.
  async restoreEvent(userId: string, event: Partial<CalendarEvent>): Promise<CalendarEvent> {
    const { id: _dropped, user_id: _uid, created_at: _ca, updated_at: _ua, is_google_event: _ige, ...fields } = event;
    return this.createEvent(userId, {
      title: fields.title ?? 'Restored event',
      description: fields.description,
      location: fields.location,
      start_time: fields.start_time ?? new Date().toISOString(),
      end_time: fields.end_time ?? new Date().toISOString(),
      all_day: fields.all_day,
      color: fields.color,
      task_id: fields.task_id,
      is_flexible_task: fields.is_flexible_task === true,
      blocks_bookings: fields.blocks_bookings,
      reminders: fields.reminders,
    }, { manualOperation: true });
  }

  async getTasksForRange(userId: string, from: Date, to: Date): Promise<CalendarTaskBlock[]> {
    // Reads go through DataService (the backend-loaded /database snapshot) rather
    // than a direct supabase query, because the row shape there is already
    // enriched with project/user joins. Direct RLS-gated reads would still work
    // post-20260522 policy backfill, just without the enrichment.
    const projectsById = new Map(DataService.getProjects().map((p: any) => [p.id, p]));
    const fromMs = from.getTime();
    const toMs = to.getTime();
    return DataService.getTasks()
      .filter((t: any) => {
        if (t.assigned_to !== userId) return false;
        if (t.status === 'completed' || t.status === 'cancelled') return false;
        if (!t.due_date) return false;
        const ms = new Date(t.due_date).getTime();
        return ms >= fromMs && ms <= toMs;
      })
      .sort((a: any, b: any) => String(a.due_date).localeCompare(String(b.due_date)))
      .map((t: any) => ({
        id: t.id,
        title: t.title,
        due_date: typeof t.due_date === 'string' ? t.due_date.split('T')[0] : t.due_date,
        priority: t.priority,
        status: t.status,
        assigned_to: t.assigned_to,
        project_id: t.project_id ?? undefined,
        project_name: t.project_id ? ((projectsById.get(t.project_id) as any)?.name ?? undefined) : undefined,
        estimated_duration_minutes: typeof t.estimated_duration_minutes === 'number' ? t.estimated_duration_minutes : undefined,
      }));
  }

  async getUnscheduledTasks(userId: string): Promise<CalendarTaskBlock[]> {
    const projectsById = new Map(DataService.getProjects().map((p: any) => [p.id, p]));
    const now = new Date();
    const scheduled = await supabase
      .from('calendar_events')
      .select('task_id,start_time,end_time,rrule,exdate,master_event_id')
      .eq('user_id', userId)
      // Recurring masters can have future slots even after their first block expired.
      .or(`end_time.gte.${now.toISOString()},rrule.not.is.null`)
      .not('task_id', 'is', null);
    if (scheduled.error) throw scheduled.error;
    const scheduledTaskIds = new Set(
      (scheduled.data ?? []).filter(event => {
        if (!event.rrule || event.master_event_id) return Date.parse(event.end_time) >= now.getTime();
        try {
          const duration = Date.parse(event.end_time) - Date.parse(event.start_time);
          if (!Number.isFinite(duration) || duration < 0) return true;
          const dtstart = new Date(event.start_time).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z$/, 'Z');
          // DTSTART must be embedded for forceset; its dtstart option is ignored by rrule.js.
          const rule = rrulestr(`DTSTART:${dtstart}\nRRULE:${event.rrule.replace(/^RRULE:/i, '')}`, { forceset: true }) as RRuleSet;
          for (const iso of event.exdate ?? []) {
            const date = new Date(iso);
            if (!Number.isFinite(date.getTime())) return true;
            rule.exdate(date);
          }
          // Include an occurrence already in progress, without expanding an unlimited series.
          return rule.after(new Date(now.getTime() - Math.max(0, duration)), true) !== null;
        } catch {
          // Malformed external recurrence must not trigger duplicate automatic bookings.
          return true;
        }
      }).map(event => event.task_id).filter(Boolean)
    );
    return DataService.getTasks()
      .filter((t: any) => {
        if (t.assigned_to !== userId) return false;
        if (!['pending', 'in_progress', 'in-progress', 'awaiting_response'].includes(t.status)) return false;
        return !scheduledTaskIds.has(t.id);
      })
      .sort((a: any, b: any) => String(a.due_date ?? '').localeCompare(String(b.due_date ?? '')))
      .map((t: any) => ({
        id: t.id,
        title: t.title,
        due_date: typeof t.due_date === 'string' ? t.due_date.split('T')[0] : t.due_date,
        priority: t.priority,
        status: t.status,
        assigned_to: t.assigned_to,
        project_id: t.project_id ?? undefined,
        project_name: t.project_id ? ((projectsById.get(t.project_id) as any)?.name ?? undefined) : undefined,
        estimated_duration_minutes: typeof t.estimated_duration_minutes === 'number' ? t.estimated_duration_minutes : undefined,
        // DataService rows are sometimes snake_case, sometimes already
        // camelCased depending on which fetch populated the cache.
        deadline_risk_flag_count: typeof t.deadline_risk_flag_count === 'number'
          ? t.deadline_risk_flag_count
          : (typeof t.deadlineRiskFlagCount === 'number' ? t.deadlineRiskFlagCount : 0),
      }));
  }

  /** Flags a task as currently at-risk, for the calendar's "slipped N times"
   *  coaching copy. Idempotent per UTC calendar day server-side, so this is
   *  safe to call on every load for every at-risk task — best-effort, never
   *  throws, since it's advisory only. */
  async flagDeadlineRisk(taskId: string): Promise<number | null> {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${API_BASE}/tasks/${taskId}/flag-deadline-risk`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
      });
      if (!res.ok) return null;
      const body = await res.json().catch(() => ({}));
      return typeof body?.deadlineRiskFlagCount === 'number' ? body.deadlineRiskFlagCount : null;
    } catch {
      return null;
    }
  }

  /** Update the task's due_date to an ISO calendar date string (YYYY-MM-DD). */
  async updateTaskDueDate(taskId: string, isoDate: string): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/tasks/${taskId}/due-date`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ due_date: isoDate }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`updateTaskDueDate failed: ${res.status} ${text}`);
    }
  }

  async updateTaskDuration(taskId: string, minutes: number): Promise<void> {
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/tasks/${taskId}/duration`, {
      method: 'PATCH',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ minutes }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`updateTaskDuration failed: ${res.status} ${text}`);
    }
  }

  async getScheduledTaskIds(userId: string): Promise<Set<string>> {
    const { data } = await supabase
      .from('calendar_events')
      .select('task_id')
      .eq('user_id', userId)
      .not('task_id', 'is', null);

    return new Set((data ?? []).map((e: any) => e.task_id).filter(Boolean));
  }

  /**
   * Creates a single-instance exception for a recurring master.
   *
   * The server atomically inserts the override row and appends the
   * originalStartTime to master.exdate so the virtual instance is suppressed.
   * Callers should reload the event list after this returns.
   */
  async createInstanceOverride(
    masterId: string,
    originalStartTime: string,
    overrides: Partial<CreateCalendarEventData>,
  ): Promise<CalendarEvent> {
    const fields = { ...overrides };
    this.prepareReminderWrite(fields);
    if (this.flexibleTaskSchema === 'legacy' && fields.is_flexible_task === false) delete fields.is_flexible_task;
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/calendar/events/${masterId}/override`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ originalStartTime, overrides: fields }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`createInstanceOverride failed: ${res.status} ${text}`);
    }
    const json = await res.json();
    return { ...json.event, reminders: json.event.reminders ?? [] } as CalendarEvent;
  }

  /**
   * Splits a recurring series at instanceStartTime.
   *
   * The old master receives UNTIL = instanceStartTime - 1 minute; a new
   * master is created starting at instanceStartTime with the same rrule.
   * Any newOverrides are applied to the new master.
   */
  async splitSeries(
    masterId: string,
    instanceStartTime: string,
    newOverrides: Partial<CreateCalendarEventData> = {},
  ): Promise<{ oldMaster: CalendarEvent; newMaster: CalendarEvent }> {
    const fields = { ...newOverrides };
    this.prepareReminderWrite(fields);
    if (this.flexibleTaskSchema === 'legacy' && fields.is_flexible_task === false) delete fields.is_flexible_task;
    const headers = await getAuthHeaders();
    const res = await fetch(`${API_BASE}/calendar/events/${masterId}/split`, {
      method: 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      body: JSON.stringify({ instanceStartTime, newOverrides: fields }),
    });
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`splitSeries failed: ${res.status} ${text}`);
    }
    const json = await res.json();
    return { oldMaster: { ...json.oldMaster, reminders: json.oldMaster.reminders ?? [] }, newMaster: { ...json.newMaster, reminders: json.newMaster.reminders ?? [] } };
  }

  /**
   * Deletes a recurring series or part of it.
   *
   *   'this'             — suppress this instance (exdate entry on master)
   *   'this-and-future'  — truncate master's rrule at instanceStartTime
   *   'all'              — delete master + all override rows
   */
  async deleteSeriesAt(
    masterId: string,
    mode: 'this' | 'this-and-future' | 'all',
    instanceStartTime?: string,
  ): Promise<void> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams({ mode });
    if (instanceStartTime) params.set('instanceStartTime', instanceStartTime);
    const res = await fetch(
      `${API_BASE}/calendar/events/${masterId}/scope?${params.toString()}`,
      { method: 'DELETE', headers },
    );
    if (!res.ok) {
      const text = await res.text().catch(() => '');
      throw new Error(`deleteSeriesAt failed: ${res.status} ${text}`);
    }
  }
}

export const CalendarService = new CalendarServiceClass();
